import React, { useRef, useState } from "react";
import { Play, Volume2, VolumeX } from "lucide-react";

// Phase 25.13an · Reusable Bloomberg-style video ad embed.
//
// Phase 25.13an-p3 — auto-detect the source video's natural aspect ratio
// via onLoadedMetadata and snap the container to match it. That means:
//   • ZERO cropping (object-contain renders the whole frame)
//   • ZERO letterbox bars (container IS the source aspect)
//   • Captions, hand gestures, head, body — all preserved
//
// We also bound the height with maxHeight so a portrait source doesn't
// dominate the entire viewport on desktop. Behaviour summary:
//   • Defaults to 16/9 while the metadata loads (no layout flash)
//   • Snaps to source aspect once known (~50-200ms after first frame)
//   • Constrained to ≤ 80vh so it always fits on screen
export default function VideoAd({
  src,
  label = "// VIDEO",
  caption,
  poster,
  autoPlay = false,
  aspect = "16/9",
  maxHeight = "80vh",
  testId = "video-ad",
}) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(autoPlay);
  const [muted, setMuted] = useState(true);
  // Track the natural aspect once the video metadata is in.
  const [computedAspect, setComputedAspect] = useState(aspect);

  const togglePlay = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  // Snap container to source aspect once metadata loads. Prevents both
  // top/bottom head crops AND side letterbox bars — every source video
  // ends up in a container that perfectly matches its native shape.
  const onLoadedMetadata = () => {
    const v = ref.current;
    if (v && v.videoWidth > 0 && v.videoHeight > 0) {
      setComputedAspect(`${v.videoWidth} / ${v.videoHeight}`);
    }
  };

  return (
    <div className="border border-zinc-800 bg-zinc-950/40" data-testid={testId}>
      {/* Chrome — Bloomberg terminal header strip */}
      <div className="px-3 py-2 border-b border-zinc-900 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest">
        <div className="flex items-center gap-2 text-[#FFB000]">
          <span className="inline-flex w-1.5 h-1.5 rounded-full bg-[#FF3B30] animate-pulse" />
          {label}
        </div>
        <div className="text-zinc-600">DARA:// FOUNDER · MESSAGE</div>
      </div>

      {/* Video frame.
          Phase 25.13an-p3 — container aspect ratio matches the source
          video's natural aspect (set via onLoadedMetadata). object-contain
          guarantees we preserve the full frame even during the brief moment
          before metadata loads. maxHeight prevents a portrait source from
          becoming taller than the viewport. */}
      <div
        className="relative bg-black cursor-pointer group mx-auto"
        style={{ aspectRatio: computedAspect, maxHeight }}
        onClick={togglePlay}
        role="button"
        aria-label="Play DARA video ad"
      >
        <video
          ref={ref}
          src={src}
          poster={poster}
          muted={muted}
          autoPlay={autoPlay}
          playsInline
          preload="metadata"
          controls={false}
          onLoadedMetadata={onLoadedMetadata}
          className="absolute inset-0 w-full h-full object-contain"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          data-testid={`${testId}-element`}
        />

        {/* Centre play overlay when paused */}
        {!playing && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-16 h-16 rounded-full border-2 border-[#FFB000] bg-black/70 flex items-center justify-center backdrop-blur group-hover:bg-[#FFB000] group-hover:text-black transition">
              <Play className="w-7 h-7 text-[#FFB000] group-hover:text-black ml-1" />
            </div>
          </div>
        )}

        {/* Bottom-right mute toggle */}
        <button
          type="button"
          onClick={toggleMute}
          className="absolute bottom-3 right-3 w-9 h-9 rounded-full border border-zinc-700 bg-black/70 backdrop-blur flex items-center justify-center hover:border-[#FFB000] hover:bg-[#FFB000]/10 transition"
          aria-label={muted ? "Unmute" : "Mute"}
          data-testid={`${testId}-mute-toggle`}
        >
          {muted ? (
            <VolumeX className="w-4 h-4 text-zinc-400" />
          ) : (
            <Volume2 className="w-4 h-4 text-[#FFB000]" />
          )}
        </button>
      </div>

      {/* Caption strip */}
      {caption && (
        <div className="px-3 py-2 border-t border-zinc-900 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          {caption}
        </div>
      )}
    </div>
  );
}
